import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ChevronDown, ChevronRight, Download, Eye, Users } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import { MonthChips, RangeShortcuts, shiftMonth, thisMonth } from '@/components/MonthPicker';

// Order-taker performance: who took the orders and how much of them has been
// dispatched to date. Orders older than the ageing window are "matured" — a
// low matured dispatch % or many matured lines never dispatched points at
// samples that were not available / not up to date at the store visit.
// View shows the report on screen; PDF / Excel download the same numbers.

const currentFYStart = () => {
  const now = new Date();
  return now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1;
};
const fyLabel = (sy) => `FY ${sy}-${String((sy + 1) % 100).padStart(2, '0')}`;
const fmtNum = (v) => Number(v || 0).toLocaleString('en-IN');
const pct = (v) => (v == null ? '—' : `${v}%`);
// Green at 80%+, amber 50–80%, red below 50%.
const pctTone = (v) => (v == null ? 'text-slate-400' : v >= 80 ? 'text-emerald-700' : v >= 50 ? 'text-amber-700' : 'text-rose-700');
const pctBar = (v) => (v == null ? 'bg-slate-200' : v >= 80 ? 'bg-emerald-500' : v >= 50 ? 'bg-amber-500' : 'bg-rose-500');
const fmtDate = (d) => {
  if (!d) return '—';
  const [y, m, dd] = String(d).slice(0, 10).split('-');
  return y && m && dd ? `${dd}-${m}-${y}` : d;
};

const FAB_GROUPS = ['Shirting', 'Suiting', 'Ethnic', 'Other'];
// Heavy pending stands out: half or more of the fabric still open = red,
// a quarter or more = amber.
const pendTone = (p) => (p == null ? '' : p >= 50 ? 'bg-rose-50 text-rose-800 font-semibold' : p >= 25 ? 'bg-amber-50 text-amber-800' : '');

function FabCells({ perf }) {
  const pbf = perf?.pending_by_fabric || {};
  return (
    <>
      {FAB_GROUPS.map(g => {
        const x = pbf[g] || {};
        return (
          <td key={g} className={`p-2 text-right tabular-nums whitespace-nowrap ${x.ordered ? pendTone(x.pending_pct) : 'text-slate-300'}`}
            title={x.ordered ? `${g}: ${fmtNum(x.pending)} of ${fmtNum(x.ordered)} units still pending` : `No ${g} ordered`}>
            {x.ordered ? <>{fmtNum(x.pending)} <span className="text-[10px] opacity-70">({pct(x.pending_pct)})</span></> : '—'}
          </td>
        );
      })}
    </>
  );
}
const FabHeads = () => FAB_GROUPS.map(g => <th key={g} className="text-right p-2">Pending {g}</th>);
const TIMING_COLS = [['le7', '≤7d'], ['d8_15', '8–15d'], ['gt15', '>15d'], ['d16_30', '16–30d'], ['gt30', '>30d']];

// The detailed columns for one row: timing buckets, dispatched / not.
function TimingCells({ perf }) {
  const tp = perf?.timing?.pct || {};
  return (
    <>
      {TIMING_COLS.map(([k]) => (
        <td key={k} className={`p-2 text-right tabular-nums ${k === 'gt15' ? 'font-semibold bg-amber-50/60' : ''}`}>{pct(tp[k])}</td>
      ))}
      <td className="p-2"><PctCell value={tp.dispatched} /></td>
      <td className={`p-2 text-right tabular-nums font-semibold ${tp.not_dispatched > 0 ? 'text-rose-700' : 'text-slate-400'}`}>{pct(tp.not_dispatched)}</td>
    </>
  );
}
function TimingHeads() {
  return (
    <>
      {TIMING_COLS.map(([k, l]) => (
        <th key={k} className={`text-right p-2 ${k === 'gt15' ? 'bg-amber-50/60' : ''}`} title={k === 'gt15' ? 'Forward deliveries: 16–30 + over 30 days' : ''}>{l}</th>
      ))}
      <th className="text-right p-2">Dispatched %</th>
      <th className="text-right p-2">Not dispatched %</th>
    </>
  );
}

function PctCell({ value }) {
  return (
    <div className="flex items-center justify-end gap-2">
      <div className="hidden sm:block w-14 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full ${pctBar(value)}`} style={{ width: `${Math.max(0, Math.min(100, value || 0))}%` }} />
      </div>
      <span className={`tabular-nums font-semibold ${pctTone(value)}`}>{pct(value)}</span>
    </div>
  );
}

export default function OrderTakerReportDialog({
  open, onOpenChange, division, takers = [], places = [], regions = [], canDownload = false, initial = {},
}) {
  const [ptype, setPtype] = useState('fy');
  const [fy, setFy] = useState(currentFYStart());
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [from, setFrom] = useState(`${currentFYStart()}-04`);
  const [to, setTo] = useState(new Date().toISOString().slice(0, 7));
  const [fromDate, setFromDate] = useState(`${new Date().toISOString().slice(0, 7)}-01`);
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));
  const [picked, setPicked] = useState([shiftMonth(thisMonth(), -1), thisMonth()]);
  const [taker, setTaker] = useState('all');
  const [place, setPlace] = useState('all');
  const [region, setRegion] = useState('all');
  // One report carries both breakdowns (owner, 2026-10-24).
  const detail = 'both';
  // Detailed: when the dispatched units left (<=7 / 8-15 / >15 / 16-30 / >30
  // days after the order) and the estimated value still pending.
  const [detailed, setDetailed] = useState(false);
  const [busy, setBusy] = useState('');
  const [view, setView] = useState(null);
  const [openTakers, setOpenTakers] = useState({});

  // Seed from whatever the page is showing each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setView(null);
    setOpenTakers({});
    if (initial.periodType) setPtype(initial.periodType);
    if (initial.fyYear) setFy(initial.fyYear);
    if (initial.monthValue) setMonth(initial.monthValue);
    if (initial.customFrom) setFrom(initial.customFrom);
    if (initial.customTo) setTo(initial.customTo);
    if (initial.customFromDate) setFromDate(initial.customFromDate);
    if (initial.customToDate) setToDate(initial.customToDate);
    if (Array.isArray(initial.pickedMonths) && initial.pickedMonths.length) setPicked(initial.pickedMonths);
    setTaker(initial.taker || 'all');
    setPlace(initial.place || 'all');
    setRegion(initial.region || 'all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const periodValue = () => (
    ptype === 'fy' ? String(fy)
      : ptype === 'custom' ? `${from}:${to}`
      : ptype === 'months' ? [...picked].sort().join(',')
      : ptype === 'custom_dates' ? `${fromDate}:${toDate}`
      : month);

  const params = (format) => {
    const p = { period_type: ptype, period_value: periodValue(), detail, format };
    if (detailed) p.timing = true;
    if (division && division !== 'all') p.division = division;
    if (taker !== 'all') p.employee_id = taker;
    if (place !== 'all') p.place = place;
    if (region !== 'all') p.region = region;
    return p;
  };

  const run = async (format) => {
    if (ptype === 'months' && picked.length === 0) { toast.error('Pick at least one month'); return; }
    setBusy(format);
    try {
      if (format === 'json') {
        const res = await axios.get(`${API}/analytics/order-summary/taker-report`, { params: params('json'), withCredentials: true, timeout: 180000 });
        setView(res.data);
        const first = (res.data?.takers || [])[0];
        setOpenTakers(first ? { [first.taker_name]: true } : {});
        return;
      }
      const res = await axios.get(`${API}/analytics/order-summary/taker-report`, {
        params: params(format), withCredentials: true, responseType: 'blob', timeout: 180000,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const stem = (view?.file_stem) || `OrderTakerPerformance_${detail}_${periodValue()}`.replace(/[^A-Za-z0-9_-]+/g, '');
      a.setAttribute('download', `${stem}.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Order-taker report ${format.toUpperCase()} downloaded`);
    } catch (err) {
      let msg = err.message || 'Report failed';
      try {
        const t = await (err.response?.data?.text?.() || Promise.resolve(''));
        if (t) { try { msg = JSON.parse(t).detail || msg; } catch { msg = t.slice(0, 200); } }
        else if (err.response?.data?.detail) msg = err.response.data.detail;
      } catch { /* keep msg */ }
      toast.error(msg);
    } finally {
      setBusy('');
    }
  };

  const fyChoices = [0, 1, 2, 3].map(i => currentFYStart() - i);
  const md = view?.mature_days || 30;

  const downloadButtons = canDownload && (
    <>
      <Button variant="outline" disabled={!!busy} onClick={() => run('excel')}>
        <Download size={14} className="mr-1" /> {busy === 'excel' ? 'Preparing…' : 'Excel'}
      </Button>
      <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={!!busy} onClick={() => run('pdf')}>
        <Download size={14} className="mr-1" /> {busy === 'pdf' ? 'Preparing…' : 'PDF'}
      </Button>
    </>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${view ? 'max-w-6xl' : 'max-w-lg'} max-h-[92vh] flex flex-col`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users size={16} className="text-indigo-600" /> Order-taker performance
          </DialogTitle>
          <DialogDescription>
            Who took the orders and how much has been dispatched to date.
            {' '}Division follows the sidebar ({division && division !== 'all' ? division : 'All divisions'}).
          </DialogDescription>
        </DialogHeader>

        {!view ? (
          <div className="space-y-3 overflow-y-auto">
            <div className="flex flex-wrap gap-1">
              {[['fy', 'Financial year'], ['month', 'Month'], ['custom', 'Month range'], ['months', 'Pick months'], ['custom_dates', 'Date range']].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setPtype(v)}
                  className={`px-3 h-8 text-xs rounded-md border ${ptype === v ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-muted-foreground'}`}>{l}</button>
              ))}
            </div>
            {ptype === 'fy' && (
              <Select value={String(fy)} onValueChange={(v) => setFy(Number(v))}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{fyChoices.map(y => <SelectItem key={y} value={String(y)}>{fyLabel(y)}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {ptype === 'month' && <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="h-9" />}
            {ptype === 'custom' && (
              <div>
                <div className="flex items-center gap-2">
                  <Input type="month" value={from} onChange={e => setFrom(e.target.value)} className="h-9" />
                  <span className="text-xs text-muted-foreground">to</span>
                  <Input type="month" value={to} onChange={e => setTo(e.target.value)} className="h-9" />
                </div>
                <RangeShortcuts className="mt-1.5" onPick={(a, b) => { setFrom(a); setTo(b); }} />
              </div>
            )}
            {ptype === 'months' && (
              <div>
                <div className="text-xs text-muted-foreground mb-1">Months ({picked.length} picked) — click to add or remove</div>
                <MonthChips value={picked} onChange={setPicked} />
              </div>
            )}
            {ptype === 'custom_dates' && (
              <div className="flex items-center gap-2">
                <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="h-9" />
                <span className="text-xs text-muted-foreground">to</span>
                <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="h-9" />
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Select value={taker} onValueChange={setTaker}>
                <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Order-taker" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All order-takers</SelectItem>
                  {takers.map(t => <SelectItem key={t.employee_id} value={t.employee_id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={region} onValueChange={setRegion}>
                <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Region" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All regions</SelectItem>
                  {regions.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={place} onValueChange={setPlace}>
                <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Place" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All places</SelectItem>
                  {places.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <p className="text-[11px] text-muted-foreground">Each order-taker's report shows a month-wise table and a customer-wise table (with location).</p>
            <label className="flex items-start gap-2 p-2.5 rounded-lg border border-indigo-200 bg-indigo-50/50 cursor-pointer">
              <input type="checkbox" className="mt-0.5 h-4 w-4 accent-indigo-600" checked={detailed} onChange={e => setDetailed(e.target.checked)} />
              <span className="text-xs">
                <span className="font-semibold text-indigo-900">Detailed dispatch timing</span>
                <span className="block text-muted-foreground mt-0.5">
                  Splits what was dispatched by how soon it left after the order — within 7 days, 8–15 days, over 15 days (16–30 and over 30: forward deliveries) —
                  plus total dispatched % and not dispatched %, with the units still pending per fabric (Shirting / Suiting / Ethnic / Other). Takes a little longer to prepare.
                </span>
              </span>
            </label>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-4 pr-1">
            <div className="text-xs text-slate-600">
              <span className="font-semibold text-slate-800">{view.period_label}</span>
              {(view.filters || []).length > 0 && <> · {view.filters.join(' · ')}</>}
              {' · '}orders older than <b>{md} days</b> count as matured
            </div>
            {/* KPI strip */}
            {(() => {
              const p = view.overall?.performance || {};
              return (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  {[
                    ['Units ordered', fmtNum(p.units_ordered)],
                    ['Units dispatched', fmtNum(p.units_dispatched)],
                    ['Dispatch % to date', pct(p.dispatch_pct), pctTone(p.dispatch_pct)],
                    [`Dispatch % (> ${md}d)`, pct(p.matured_dispatch_pct), pctTone(p.matured_dispatch_pct)],
                    ['Matured lines never dispatched', `${fmtNum(p.matured_never_dispatched_lines)} (${pct(p.matured_never_dispatched_pct)})`],
                    ...(view.timing ? [
                      ['Dispatched > 15 days (forward)', pct(p.timing?.pct?.gt15)],
                      ['Not dispatched yet', pct(p.timing?.pct?.not_dispatched), 'text-rose-700'],
                    ] : []),
                    ...FAB_GROUPS.filter(g => (p.pending_by_fabric?.[g]?.ordered || 0) > 0).map(g => {
                      const x = p.pending_by_fabric[g];
                      return [`${g} pending`, `${fmtNum(x.pending)} (${pct(x.pending_pct)})`,
                        x.pending_pct >= 50 ? 'text-rose-700' : x.pending_pct >= 25 ? 'text-amber-700' : 'text-slate-800'];
                    }),
                  ].map(([l, v, tone]) => (
                    <div key={l} className="rounded-lg border bg-slate-50 px-3 py-2">
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{l}</div>
                      <div className={`text-lg font-bold tabular-nums ${tone || 'text-slate-800'}`}>{v}</div>
                    </div>
                  ))}
                </div>
              );
            })()}
            {/* Summary per taker */}
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-indigo-50 text-[10px] uppercase tracking-wide text-indigo-900">
                    <th className="text-left p-2">Order-taker</th>
                    <th className="text-right p-2">Customers</th>
                    {view.timing ? (
                      <>
                        <th className="text-right p-2">Units ordered</th>
                        <TimingHeads />
                        <FabHeads />
                      </>
                    ) : (
                      <>
                        <th className="text-right p-2">Orders</th>
                        <th className="text-right p-2">Units ordered</th>
                        <th className="text-right p-2">Dispatched</th>
                        <th className="text-right p-2">Dispatch %</th>
                        <th className="text-right p-2">Disp % (&gt; {md}d)</th>
                        <th className="text-right p-2">Never dispatched</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {(view.takers || []).map(t => {
                    const p = t.performance || {};
                    return (
                      <tr key={t.taker_name} className="border-t hover:bg-slate-50 cursor-pointer"
                        onClick={() => {
                          setOpenTakers(prev => ({ ...prev, [t.taker_name]: true }));
                          document.getElementById(`otr-${t.taker_name}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}>
                        <td className="p-2 font-medium text-indigo-700">{t.taker_name}</td>
                        <td className="p-2 text-right tabular-nums">{fmtNum(t.customer_count)}</td>
                        {view.timing ? (
                          <>
                            <td className="p-2 text-right tabular-nums">{fmtNum(p.units_ordered)}</td>
                            <TimingCells perf={p} />
                            <FabCells perf={p} />
                          </>
                        ) : (
                          <>
                            <td className="p-2 text-right tabular-nums">{fmtNum(p.orders)}</td>
                            <td className="p-2 text-right tabular-nums">{fmtNum(p.units_ordered)}</td>
                            <td className="p-2 text-right tabular-nums">{fmtNum(p.units_dispatched)}</td>
                            <td className="p-2"><PctCell value={p.dispatch_pct} /></td>
                            <td className="p-2"><PctCell value={p.matured_dispatch_pct} /></td>
                            <td className="p-2 text-right tabular-nums">{fmtNum(p.matured_never_dispatched_lines)} <span className="text-muted-foreground">({pct(p.matured_never_dispatched_pct)})</span></td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Needs attention: where the pending quantum sits */}
            {((view.attention?.regions || []).length > 0 || (view.attention?.taker_fabric || []).length > 0) && (
              <div className="border border-rose-200 rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-rose-50 text-sm font-semibold text-rose-900">Needs attention — where the pending quantum sits</div>
                <div className="grid lg:grid-cols-2 gap-0 lg:divide-x">
                  {(view.attention?.regions || []).length > 0 && (
                    <div className="overflow-x-auto">
                      <div className="px-3 pt-2 text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">By region and fabric</div>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-[10px] uppercase tracking-wide text-muted-foreground border-b">
                            <th className="text-left p-2">Region</th>
                            <FabHeads />
                            <th className="text-right p-2">Total pending</th>
                          </tr>
                        </thead>
                        <tbody>
                          {view.attention.regions.map(r => (
                            <tr key={r.region} className="border-t">
                              <td className="p-2 font-medium">{r.region}</td>
                              <FabCells perf={{ pending_by_fabric: r.pending_by_fabric }} />
                              <td className="p-2 text-right tabular-nums font-semibold">{fmtNum(r.pending_total)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {(view.attention?.taker_fabric || []).length > 0 && (
                    <div className="overflow-x-auto">
                      <div className="px-3 pt-2 text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">Largest pending blocks — order-taker × fabric</div>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-[10px] uppercase tracking-wide text-muted-foreground border-b">
                            <th className="text-left p-2">Order-taker</th>
                            <th className="text-left p-2">Fabric</th>
                            <th className="text-right p-2">Pending units</th>
                            <th className="text-right p-2">Of ordered</th>
                            <th className="text-right p-2">Pending %</th>
                          </tr>
                        </thead>
                        <tbody>
                          {view.attention.taker_fabric.map((a, i) => (
                            <tr key={`${a.taker_name}-${a.fabric}-${i}`} className="border-t">
                              <td className="p-2 font-medium">{a.taker_name}</td>
                              <td className="p-2">{a.fabric}</td>
                              <td className="p-2 text-right tabular-nums font-semibold">{fmtNum(a.pending)}</td>
                              <td className="p-2 text-right tabular-nums text-muted-foreground">{fmtNum(a.ordered)}</td>
                              <td className={`p-2 text-right tabular-nums ${pendTone(a.pending_pct)}`}>{pct(a.pending_pct)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Per-taker detail */}
            {(view.takers || []).map(t => {
              const p = t.performance || {};
              const isOpen = !!openTakers[t.taker_name];
              return (
                <div key={t.taker_name} id={`otr-${t.taker_name}`} className="border rounded-lg overflow-hidden">
                  <button type="button" className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 text-left"
                    onClick={() => setOpenTakers(prev => ({ ...prev, [t.taker_name]: !isOpen }))}>
                    <span className="flex items-center gap-2 text-sm font-semibold">
                      {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}{t.taker_name}
                      <span className="text-xs font-normal text-muted-foreground">{fmtNum(t.customer_count)} customers · {fmtNum(p.units_ordered)} units</span>
                    </span>
                    <span className="text-xs">
                      <span className={`font-semibold ${pctTone(p.dispatch_pct)}`}>{pct(p.dispatch_pct)}</span> to date ·{' '}
                      <span className={`font-semibold ${pctTone(p.matured_dispatch_pct)}`}>{pct(p.matured_dispatch_pct)}</span> matured
                    </span>
                  </button>
                  {isOpen && (
                    <div className="divide-y">
                      {['months', 'customers'].map(kind => {
                        const rows = kind === 'months' ? (t.months || []) : (t.customers || []);
                        if (!rows.length) return null;
                        return (
                          <div key={kind} className="overflow-x-auto">
                            <div className="px-3 pt-2 text-[10px] uppercase tracking-wide font-semibold text-indigo-700">
                              {kind === 'months' ? 'Month-wise' : 'Customer-wise'}
                            </div>
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-[10px] uppercase tracking-wide text-muted-foreground border-b">
                                  {kind === 'months' ? (
                                    <>
                                      <th className="text-left p-2">Month</th>
                                      {!view.timing && <th className="text-right p-2">Orders</th>}
                                      {!view.timing && <th className="text-right p-2">Lines</th>}
                                    </>
                                  ) : (
                                    <>
                                      <th className="text-left p-2">Customer</th>
                                      <th className="text-left p-2">Location</th>
                                      {!view.timing && <th className="text-left p-2">Region</th>}
                                      {!view.timing && <th className="text-right p-2">Orders</th>}
                                    </>
                                  )}
                                  <th className="text-right p-2">Units ordered</th>
                                  {view.timing ? (
                                    <>
                                      <TimingHeads />
                                      <FabHeads />
                                    </>
                                  ) : (
                                    <>
                                      <th className="text-right p-2">Dispatched</th>
                                      <th className="text-right p-2">Dispatch %</th>
                                      <th className="text-right p-2">Disp % (&gt; {md}d)</th>
                                      <th className="text-right p-2">Never dispatched</th>
                                    </>
                                  )}
                                  {kind !== 'months' && <th className="text-right p-2">Last order</th>}
                                </tr>
                              </thead>
                              <tbody>
                                {rows.map((r, i) => {
                                  const rp = r.performance || {};
                                  return (
                                    <tr key={`${kind}-${r.customer_code || r.month || i}`} className="border-t">
                                      {kind === 'months' ? (
                                        <>
                                          <td className="p-2 font-medium">{r.month_label || r.month}</td>
                                          {!view.timing && <td className="p-2 text-right tabular-nums">{fmtNum(rp.orders)}</td>}
                                          {!view.timing && <td className="p-2 text-right tabular-nums">{fmtNum(rp.lines)}</td>}
                                        </>
                                      ) : (
                                        <>
                                          <td className="p-2"><span className="font-medium">{r.customer_name}</span> <span className="text-muted-foreground">({r.customer_code})</span></td>
                                          <td className="p-2 text-sky-700">{r.location || '—'}</td>
                                          {!view.timing && <td className="p-2 text-muted-foreground">{r.region || '—'}</td>}
                                          {!view.timing && <td className="p-2 text-right tabular-nums">{fmtNum(rp.orders)}</td>}
                                        </>
                                      )}
                                      <td className="p-2 text-right tabular-nums">{fmtNum(rp.units_ordered)}</td>
                                      {view.timing ? (
                                        <>
                                          <TimingCells perf={rp} />
                                          <FabCells perf={rp} />
                                        </>
                                      ) : (
                                        <>
                                          <td className="p-2 text-right tabular-nums">{fmtNum(rp.units_dispatched)}</td>
                                          <td className="p-2"><PctCell value={rp.dispatch_pct} /></td>
                                          <td className="p-2"><PctCell value={rp.matured_dispatch_pct} /></td>
                                          <td className="p-2 text-right tabular-nums">{fmtNum(rp.matured_never_dispatched_lines)}</td>
                                        </>
                                      )}
                                      {kind !== 'months' && <td className="p-2 text-right tabular-nums text-muted-foreground">{fmtDate(rp.last_order_date)}</td>}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            {(view.takers || []).length === 0 && <p className="text-sm text-muted-foreground text-center py-8">No orders for these filters.</p>}
            <p className="text-[11px] text-muted-foreground">{view.note}</p>
          </div>
        )}

        <DialogFooter className="gap-2 pt-2 border-t border-slate-100">
          {view ? (
            <Button variant="outline" onClick={() => setView(null)} disabled={!!busy} className="mr-auto">
              <ArrowLeft size={14} className="mr-1" /> Change filters
            </Button>
          ) : (
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={!!busy}>Cancel</Button>
          )}
          {!view && (
            <Button variant="outline" className="text-indigo-700 border-indigo-300 hover:bg-indigo-50" disabled={!!busy} onClick={() => run('json')}>
              <Eye size={14} className="mr-1" /> {busy === 'json' ? 'Loading…' : 'View'}
            </Button>
          )}
          {downloadButtons}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
